import React, { memo, useState, useCallback, useEffect, useMemo } from 'react'

// import ReactTooltip from 'react-tooltip'
// import Tooltip from 'react-tooltip-lite'

import styles from './styles.module.scss'
import cn from 'classnames'

import CheckBox from '../../../../../UI/CheckBox/CheckBox'
import Button from '../../../../../UI/Button/Button'

import { ReactComponent as FlagIcon } from './icons/flag.svg'
import { ReactComponent as BellIcon } from './icons/bell.svg'
import { ReactComponent as BellFillIcon } from './icons/bellFill.svg'

import { setEmailSubscription } from '../../../../../api/postApi'
import { deleteEmailSubscription } from '../../../../../api/deleteApi'
import { useNotify } from '../../../../../hooks/useNotify'
import { getDateByUserFormat } from '../../../../../helpers/timeHelper'
import { centToDollar } from '../../utils'
import { StyledTooltip } from '../../../../../UI/Tooltip/Tooltip.config'
import useMediaQuery from '@mui/material/useMediaQuery'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import moment from 'moment'

const MAX_DESCRIPTION_LENGTH = 174
const ACTION = {
  SEND: 'send',
  NOT_SEND: 'not send',
}

const ProductItem = memo(
  ({
    data,
    onCheckBoxClick,
    serviceEmailSubscription,
    updateProductsByEmailSubscription,
    markedPaidIds,
    markedUnpaidIds,
    unProlongedProducts,
    currentProductId,
    isPriceLoading,
  }) => {
    const [loading, setLoading] = useState(false)
    const canHover = useMediaQuery('(hover: hover) and (pointer: fine)')
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)

    const { notify } = useNotify()

    const {
      id,
      picture,
      name,
      prices,
      description,
      is_soon,
      is_free,
      is_testing,
      email_subscription,
      referral_data,
    } = data

    const handleEmailSubscriptionClick = async () => {
      setLoading(true)

      try {
        if (email_subscription) {
          await deleteEmailSubscription(id)

          updateProductsByEmailSubscription(id)
          serviceEmailSubscription(ACTION.NOT_SEND, name)

          return
        }

        await setEmailSubscription(id)

        updateProductsByEmailSubscription(id)
        serviceEmailSubscription(ACTION.SEND, name)
      } catch (error) {
        notify.errorsList(error.errors)
      } finally {
        setLoading(false)
      }
    }

    const isProductPurchased =
      markedPaidIds.includes(id) || referral_data?.by_points_next || referral_data?.by_points_now

    const isChecked =
      [...markedPaidIds, ...markedUnpaidIds].includes(id) ||
      referral_data?.by_points_next ||
      referral_data?.by_points_now

    const sliceString = description.split(' ').reduce((acc, cur) => {
      if (acc.length >= MAX_DESCRIPTION_LENGTH) return acc

      return `${acc} ${cur}`
    }, '')
    const restString = description.slice(sliceString.length)
    const isLongDescription = description.length > MAX_DESCRIPTION_LENGTH
    const newDescription = isLongDescription ? sliceString : description

    const unProlongedProduct = unProlongedProducts.find(({ product }) => product.id === id)
    const expDateByUserFormat = (() => {
      if (!unProlongedProduct?.expire_datetime) return null

      return getDateByUserFormat(moment(unProlongedProduct?.expire_datetime)).replaceAll('/', '.')
    })()
    const isDisabled = unProlongedProduct || (is_free && !referral_data?.by_points_now)

    const handleTooltipToggle = useCallback(() => {
      if (canHover) return
      setIsTooltipOpen((prev) => !prev)
    }, [canHover])

    const handleTooltipClose = useCallback(() => {
      if (canHover) return
      setIsTooltipOpen(false)
    }, [canHover])

    useEffect(() => {
      if (canHover) return
      if (!isTooltipOpen) return

      const closeOnScroll = () => {
        setIsTooltipOpen(false)
      }

      document.addEventListener('scroll', closeOnScroll, true)
      window.addEventListener('scroll', closeOnScroll, true)
      window.addEventListener('wheel', closeOnScroll, { passive: true })
      document.addEventListener('touchmove', closeOnScroll, { passive: true })
      document.addEventListener('pointermove', closeOnScroll, { passive: true })

      return () => {
        document.removeEventListener('scroll', closeOnScroll, true)
        window.removeEventListener('scroll', closeOnScroll, true)
        window.removeEventListener('wheel', closeOnScroll)
        document.removeEventListener('touchmove', closeOnScroll)
        document.removeEventListener('pointermove', closeOnScroll)
      }
    }, [canHover, isTooltipOpen])

    const popperProps = useMemo(
      () => ({
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'flip',
            options: {
              padding: 8,
            },
          },
        ],
      }),
      []
    )

    const tooltipProps = canHover
      ? {
          arrow: true,
          placement: 'top',
          PopperProps: popperProps,
        }
      : {
          arrow: true,
          placement: 'top',
          open: isTooltipOpen,
          disableHoverListener: true,
          disableFocusListener: true,
          disableTouchListener: true,
          PopperProps: popperProps,
          onClose: handleTooltipClose,
        }

    const more = (
      <>
        {restString.length !== 0 && (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <StyledTooltip className={styles.tooltipMore} title={restString} {...tooltipProps}>
              <span data-tip={true} data-for={`${name}-${id}`} className={styles.more} onClick={handleTooltipToggle}>
                more
              </span>
            </StyledTooltip>
          </ClickAwayListener>
        )}
      </>
    )
    const tooltipContent = (
      <p className={styles.tooltipBell}>
        <span>We will send you an email once the</span> <br />
        <b>{name}</b> <span>is launched</span>
      </p>
    )

    return (
      <article className={cn(styles.productItem, { [styles.productItem__purchased]: isProductPurchased })}>
        <div className={styles.head}>
          <div className={styles.iconWrapper}>
            <img src={picture.file_mini} alt='product icon' />
          </div>

          <div className={styles.mainInfo}>
            <h4>{name}</h4>

            <div className={styles.labels}>
              {unProlongedProduct && (
                <label className={cn(styles.label, styles.label__subscriptionValidDate)}>
                  <FlagIcon />

                  <span>{expDateByUserFormat}</span>
                </label>
              )}

              {is_soon && <label className={cn(styles.label, styles.label__soon)}>soon</label>}

              {is_free && !referral_data?.by_points_now && !referral_data?.by_points_next && (
                <label className={cn(styles.label, styles.label__priceFree)}>free</label>
              )}

              {(referral_data?.by_points_now || referral_data?.by_points) && (
                <label className={cn(styles.label, styles.label__priceFree)}>by points</label>
              )}

              {is_testing && !is_soon && (
                <label className={cn(styles.label, styles.label__testing)}>testing in progress</label>
              )}

              {!is_free && (
                <label className={cn(styles.label, styles.label__price)}>{`${centToDollar(prices.price)}${
                  prices.currency.symbol
                }/mth`}</label>
              )}
            </div>
          </div>

          {!is_soon && (
            <CheckBox
              id={id}
              theme='bordered-white'
              checked={isChecked}
              onClick={onCheckBoxClick}
              className={cn(styles.checkBox, {
                [styles.checkBox__disabled]: isDisabled,
              })}
              disabled={(isPriceLoading && currentProductId === id) || isDisabled}
            />
          )}

          {is_soon && (
            <Button
              theme='text'
              type='button'
              className={styles.bell}
              onClick={handleEmailSubscriptionClick}
              disabled={isDisabled || loading}
            >
              <StyledTooltip title={tooltipContent}>
                {Boolean(email_subscription) ? <BellFillIcon /> : <BellIcon />}
              </StyledTooltip>
            </Button>
          )}
        </div>

        <div className={styles.description}>
          {newDescription}
          {isLongDescription && more}
        </div>
      </article>
    )
  }
)

export default ProductItem
